import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { SystemRole } from "@/lib/constants";

export interface CreateEmployeeInput {
  full_name: string;
  email: string;
  role: SystemRole;
  specialization?: string | null;
  // Sensitive rate columns are written via admin_set_employee_rates RPC after insert
  hourly_cost_rate?: number | null;
  monthly_capacity_hours?: number | null;
  hire_date?: string | null;
  job_title?: string | null;
  phone?: string | null;
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEmployeeInput) => {
      // Insert base user row — omit revoked columns (hourly_cost_rate, hire_date)
      const { data, error } = await supabase
        .from("users")
        .insert({
          full_name: input.full_name,
          email: input.email,
          role: input.role,
          specialization: input.specialization ?? null,
          monthly_capacity_hours: input.monthly_capacity_hours ?? 141.25,
          job_title: input.job_title ?? null,
          phone: input.phone ?? null,
          is_active: true,
        })
        .select()
        .single();
      if (error) throw error;

      // Write rate columns via SECURITY DEFINER RPC (admin only)
      if (
        input.hourly_cost_rate !== undefined ||
        input.hire_date !== undefined
      ) {
        const { error: rpcError } = await supabase.rpc("admin_set_employee_rates", {
          p_user_id: data.id,
          p_cost: input.hourly_cost_rate ?? null,
          p_sales: null,
          p_hire: input.hire_date ?? null,
        });
        if (rpcError) throw new Error(`Rater kunne ikke settes: ${rpcError.message}`);
      }

      return data;
    },
    onSuccess: async (data) => {
      await invalidateFor(qc, "users", { userId: data?.id });
    },
  });
}
